/**
 * CourseCEInfoSection
 *
 * Admin form section on the course settings page for editing the
 * continuing-education metadata that powers the learner-facing
 * "CE Information" tab on the course detail page. CE Hours is a
 * separate course scalar edited in the parent tab; this section
 * captures the AGD / PACE block, including the badge image.
 *
 * Form state is owned by the parent so the course's existing dirty-
 * tracking + save flow stay centralized. We just render the fields.
 */

import { useRef } from 'react';
import { useSnackbar } from 'notistack';
import { Autocomplete, Box, Button, TextField, Typography } from '../ui';
import { CourseCEInfo } from '../../utils/courseCEInfo';
import { alert, uploadFile } from '../../hooks/function';
import defaultPaceBadge from '../../assets/images/agd-pace-badge.png';

export interface CourseCEInfoSectionProps {
  value: CourseCEInfo;
  onChange: (next: CourseCEInfo) => void;
}

/**
 * AGD subject codes — top-level categories first, then sub-codes
 * grouped under the parent they belong to (130 Electives, 340
 * Anesthesia, 550 Practice Management). `value` is the string we
 * store and surface; `group` drives MUI Autocomplete's grouping so
 * the parent / child relationship reads visually in the dropdown.
 */
interface AGDCode {
  value: string;
  group: string;
}
const SUBJECT_CODES_GROUP = 'Subject codes';
const AGD_SUBJECT_CODES: AGDCode[] = [
  // Top-level subject codes
  { value: '010 Basic Sciences', group: SUBJECT_CODES_GROUP },
  { value: '070 Endodontics', group: SUBJECT_CODES_GROUP },
  { value: '130 Electives', group: SUBJECT_CODES_GROUP },
  { value: '180 Myofascial Pain/Occlusion', group: SUBJECT_CODES_GROUP },
  { value: '200 Orofacial Pain', group: SUBJECT_CODES_GROUP },
  { value: '250 Operative (Restorative) Dentistry', group: SUBJECT_CODES_GROUP },
  { value: '310 Oral and Maxillofacial Surgery', group: SUBJECT_CODES_GROUP },
  { value: '340 Anesthesia and Pain Management', group: SUBJECT_CODES_GROUP },
  { value: '370 Orthodontics', group: SUBJECT_CODES_GROUP },
  { value: '430 Pediatric Dentistry', group: SUBJECT_CODES_GROUP },
  { value: '490 Periodontics', group: SUBJECT_CODES_GROUP },
  { value: '550 Practice Management and Human Relations', group: SUBJECT_CODES_GROUP },
  { value: '610 Fixed Prosthodontics', group: SUBJECT_CODES_GROUP },
  { value: '670 Removable Prosthodontics', group: SUBJECT_CODES_GROUP },
  { value: '690 Implants', group: SUBJECT_CODES_GROUP },
  { value: '730 Oral Medicine, Oral Diagnosis, Oral Pathology', group: SUBJECT_CODES_GROUP },
  { value: '750 Special Patient Care', group: SUBJECT_CODES_GROUP },
  { value: '770 Self-Improvement', group: SUBJECT_CODES_GROUP },
  { value: '780 Esthetics/Cosmetic Dentistry', group: SUBJECT_CODES_GROUP },
  // Sub-codes under 130 Electives
  { value: '135 Laser therapy/electrosurgery', group: '130 Electives' },
  { value: '138 Dental photography', group: '130 Electives' },
  { value: '142 Medical emergency training and CPR', group: '130 Electives' },
  { value: '144 Table clinics', group: '130 Electives' },
  { value: '145 Forensic dentistry', group: '130 Electives' },
  { value: '148 Infection control', group: '130 Electives' },
  { value: '149 Multi-disciplinary topics', group: '130 Electives' },
  { value: '150 Health medicine and nutrition', group: '130 Electives' },
  { value: '153 Psychosocial anxiety & fear in dentistry', group: '130 Electives' },
  { value: '154 Sports dentistry', group: '130 Electives' },
  { value: '155 Child abuse identification and reporting', group: '130 Electives' },
  { value: '156 Domestic violence', group: '130 Electives' },
  { value: '157 Substance abuse', group: '130 Electives' },
  { value: '158 Tobacco Cessation', group: '130 Electives' },
  { value: '159 Prevention of medical errors', group: '130 Electives' },
  { value: '161 Dentinal hypersensitivity & treatment', group: '130 Electives' },
  { value: '166 Human trafficking', group: '130 Electives' },
  // Sub-code under 340 Anesthesia and Pain Management
  { value: '346 Opioid, controlled substances and pain management', group: '340 Anesthesia and Pain Management' },
  // Sub-codes under 550 Practice Management and Human Relations
  { value: '555 Ethics in Dentistry', group: '550 Practice Management and Human Relations' },
  { value: '558 Diversity Inclusion/Cultural Competence', group: '550 Practice Management and Human Relations' },
  { value: '563 California Dental Practice Act', group: '550 Practice Management and Human Relations' },
  { value: '564 NY Ethics and Jurisprudence', group: '550 Practice Management and Human Relations' },
  { value: '565 Documentation and Risk Management', group: '550 Practice Management and Human Relations' },
  { value: '566 HIPAA', group: '550 Practice Management and Human Relations' },
  { value: '567 Electronic health record and policies', group: '550 Practice Management and Human Relations' },
];

const AGD_GROUP_BY = new Map(AGD_SUBJECT_CODES.map((o) => [o.value, o.group]));
const AGD_OPTIONS = AGD_SUBJECT_CODES.map((o) => o.value);

export default function CourseCEInfoSection({ value, onChange }: CourseCEInfoSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const patch = (field: keyof CourseCEInfo, raw: string) =>
    onChange({ ...value, [field]: raw });

  // Resolved badge URL — admin's custom upload wins; otherwise the
  // bundled AGD PACE asset shows. Same fallback the learner CE tab uses.
  const badgeSrc = value.paceBadgeUrl || defaultPaceBadge;
  const isCustomBadge = !!value.paceBadgeUrl;

  const handleBadgeFileSelected = async (file: File) => {
    try {
      const result = await uploadFile(file);
      if (result?.uploadedUrl) {
        patch('paceBadgeUrl', result.uploadedUrl);
      } else {
        alert(enqueueSnackbar, 'Badge upload failed', 'error');
      }
    } catch (e) {
      console.error('Badge upload error:', e);
      alert(enqueueSnackbar, 'Badge upload failed', 'error');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        These fields render in the learner's "CE Information" tab on
        the course detail page. Leave a row blank to hide it.
      </Typography>

      {/* PACE badge — defaults to the bundled AGD seal; admins can
          replace it per course (different brand, custom seal, etc.). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 1.5,
          borderRadius: 1.5,
          border: '1px dashed',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          flexWrap: 'wrap',
        }}
      >
        <Box
          component="img"
          src={badgeSrc}
          alt="PACE accreditation badge"
          sx={{
            height: 72,
            width: 'auto',
            maxWidth: 220,
            objectFit: 'contain',
            display: 'block',
            bgcolor: '#fff',
            borderRadius: 1,
            p: 1,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Accreditation badge
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {isCustomBadge
              ? 'Using a custom badge for this course.'
              : 'Using the default AGD PACE badge. Replace it to show a different seal here.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleBadgeFileSelected(f);
              }}
            />
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => fileInputRef.current?.click()}
            >
              {isCustomBadge ? 'Replace badge' : 'Upload custom badge'}
            </Button>
            {isCustomBadge && (
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={() => patch('paceBadgeUrl', '')}
              >
                Reset to default
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        {/* AGD subject code — searchable dropdown of curated codes
            with sub-codes grouped under their parent (130 Electives,
            340 Anesthesia, 550 Practice Management). `freeSolo` lets
            admins type an off-list value if the AGD adds a new code
            before this list catches up. */}
        <Autocomplete
          freeSolo
          options={AGD_OPTIONS}
          groupBy={(option) => AGD_GROUP_BY.get(option) || SUBJECT_CODES_GROUP}
          value={value.agdSubjectCode || ''}
          onChange={(_e, next) => patch('agdSubjectCode', (next as string) || '')}
          onInputChange={(_e, next, reason) => {
            // Mirror keystrokes into the form so a typed-but-not-
            // committed value still saves with the rest of the form.
            if (reason === 'input') patch('agdSubjectCode', next);
          }}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label="AGD subject code"
              placeholder="Type to search e.g. 550, HIPAA"
              helperText="AGD subject area this course covers."
            />
          )}
        />
        <TextField
          label="AGD PACE Provider ID"
          value={value.paceProviderId}
          onChange={(e) => patch('paceProviderId', e.target.value)}
          placeholder="e.g. 304738"
          size="small"
          helperText="Number printed on the PACE seal."
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        <TextField
          label="Educational method"
          value={value.educationalMethod}
          onChange={(e) => patch('educationalMethod', e.target.value)}
          placeholder="e.g. Lecture, video, self-paced quiz"
          size="small"
        />
        <TextField
          label="Location"
          value={value.location}
          onChange={(e) => patch('location', e.target.value)}
          placeholder='Defaults to "Online" — override for in-person events.'
          size="small"
          helperText="Where the CE is delivered. Defaults to Online."
        />
      </Box>

      <TextField
        label="Accreditation statement"
        value={value.accreditationStatement}
        onChange={(e) => patch('accreditationStatement', e.target.value)}
        placeholder="The AGD / PACE accreditation boilerplate displayed below the seal."
        multiline
        minRows={3}
      />

      <TextField
        label="Disclosure statement"
        value={value.disclosureStatement}
        onChange={(e) => patch('disclosureStatement', e.target.value)}
        placeholder="Conflicts of interest, commercial support, off-label use disclosures."
        multiline
        minRows={3}
      />
    </Box>
  );
}
